import * as GlobalS from "@/components/styled";
import { GUTTER_SIZE } from "@/enums";
import { IProject, ISku } from "@/interfaces";
import { useUpdateProjectSkuMutation } from "@/services";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Space,
  Typography,
} from "antd";
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { mergeUpdatedSkus } from "../helper/mergeUpdatedSkus";

const SKU_MAX_COUNT = 10;
const { Text } = Typography;

interface ISkuCardProps {
  setFormValue?: (name: string, value: ISku[]) => void;
  initialValuesFromForm?: ISku[];
  readOnly?: boolean;
  skuData?: ISku[];
  setProject?: Dispatch<SetStateAction<Partial<IProject> | undefined>>;
  intakeId?: string;
}

const SkuCard = ({
  setFormValue,
  initialValuesFromForm,
  readOnly,
  skuData,
  setProject,
  intakeId,
}: ISkuCardProps) => {
  const intl = useIntl();

  const { projectId } = useParams<{ projectId: string }>();

  const [inputValue, setInputValue] = useState("");
  const [isDeleting, setIsDeleting] = useState<Record<string, boolean>>({});
  // for saving from form
  const [skuDataLocal, setSkuDataLocal] = useState<ISku[]>([]);

  const [updateProjectSku, { isLoading }] = useUpdateProjectSkuMutation();

  const id = projectId ? projectId : intakeId;

  useEffect(() => {
    if (initialValuesFromForm) {
      setSkuDataLocal(initialValuesFromForm);
    }
  }, [projectId, initialValuesFromForm]);

  useEffect(() => {
    if (setFormValue) {
      setFormValue("stockKeepingUnits", skuDataLocal);
      setInputValue("");
    }
  }, [skuDataLocal, setFormValue]);

  const handleAdd = () => {
    if (!setFormValue && id) {
      // save to project
      const skus = [...(skuData ?? []), { name: inputValue.trim() }];
      updateProjectSku({ projectId: id, skus })
        .unwrap()
        .then((updatedSkus) => {
          setProject?.((prev) => mergeUpdatedSkus(prev, updatedSkus));
          setInputValue("");
        })
        .catch(() => {
          toast.error(
            intl.formatMessage({ id: "general.toastSomethingWentWrong" })
          );
        });
    } else {
      // update form instead
      const units = [...skuDataLocal, { name: inputValue.trim() }];
      setSkuDataLocal(units);
    }
  };

  const handleDelete = (skuId: string | undefined, index: number) => {
    if (!setFormValue && id) {
      // save delete to project
      const skus = (skuData ?? []).filter((x) => x.id !== skuId);
      setIsDeleting((prev) => ({ ...prev, [String(skuId)]: true }));
      updateProjectSku({ projectId: id, skus })
        .unwrap()
        .then((updatedSkus) => {
          setProject?.((prev) => mergeUpdatedSkus(prev, updatedSkus));
          setIsDeleting((prev) => ({ ...prev, [String(skuId)]: false }));
        });
    } else {
      // remove from form data
      setSkuDataLocal(
        skuDataLocal.filter((sku) => sku !== skuDataLocal[index])
      );
    }
  };

  const onAddChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const viewSkuList = () => {
    if (!setFormValue && id) {
      const rows = skuData?.map((data, index) => {
        const indexLabel = `0${(index += 1)}`.slice(
          index.toString().length - 1
        );
        return displayRow(data, indexLabel, index);
      });
      return rows;
    }
    // means we are editing from form and should check from local!
    const rows = skuDataLocal.map((data, index) => {
      const indexLabel = `0${index + 1}`.slice(
        (index + 1).toString().length - 1
      );
      return displayRow(data, indexLabel, index);
    });
    return rows;
  };

  const displayRow = (sku: ISku, index: string, orderIndex: number) => (
    <Row
      key={`${orderIndex}-${sku.id}`}
      wrap={false}
      style={{ wordBreak: "break-all", paddingBottom: 0 }}
    >
      <Col flex="auto">
        <Space>
          <Text strong>{index}.</Text>
          <Text>{sku.name}</Text>
        </Space>
      </Col>
      {(!readOnly || !id) && (
        <Popconfirm
          title={intl.formatMessage({
            id: "sku.actionDelete",
          })}
          okText={intl.formatMessage({ id: "general.delete" })}
          cancelText={intl.formatMessage({ id: "general.cancel" })}
          onConfirm={() => handleDelete(sku.id, orderIndex)}
          data-test-id="sku-delete-confirm"
          placement="topRight"
        >
          <Button
            type="link"
            icon={<FontAwesomeIcon icon={faTrash} />}
            data-test-id="sku-delete-btn"
            loading={isDeleting?.[String(sku.id)]}
            disabled={isLoading}
          />
        </Popconfirm>
      )}
    </Row>
  );

  const renderInputField = (length = 0) => {
    if ((length >= SKU_MAX_COUNT || readOnly) && id) {
      return;
    }

    return (
      <>
        <Row wrap={false} gutter={GUTTER_SIZE.small} align="bottom">
          <Col flex="auto">
            <Form.Item
              label={
                !setFormValue && id
                  ? intl.formatMessage({ id: "sku.fieldLabel" })
                  : null
              }
            >
              <Input
                maxLength={20}
                onChange={onAddChange}
                data-test-id="sku-add-new-input"
                value={inputValue}
                allowClear
                placeholder={intl.formatMessage({ id: "sku.inputPlaceHolder" })}
              />
            </Form.Item>
          </Col>
          <Col flex="none">
            <Form.Item>
              <Button
                data-test-id="sku-add-btn"
                type="primary"
                onClick={handleAdd}
                disabled={!inputValue}
                loading={isLoading}
              >
                {intl.formatMessage({ id: "general.add" })}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      {!setFormValue && id ? (
        <GlobalS.GrayCard
          size="small"
          title={intl.formatMessage({ id: "project.skuTitle" })}
        >
          <Row>
            <Text>{intl.formatMessage({ id: "sku.description" })}</Text>
          </Row>
          {renderInputField(skuData?.length)}
          {viewSkuList()}
        </GlobalS.GrayCard>
      ) : (
        <>
          <Row>
            <Text>{intl.formatMessage({ id: "sku.description" })}</Text>
            <Text>{intl.formatMessage({ id: "sku.fieldLabel" })}</Text>
          </Row>
          {renderInputField(skuDataLocal?.length)}
          {viewSkuList()}
        </>
      )}
    </>
  );
};

export { SkuCard };
